// ============================================
// SammyGas - Servicio de Autenticación
// ============================================

import api from './api';
import { STORAGE_KEYS } from '../utils/constants';

/**
 * Servicio para manejar todas las operaciones de autenticación
 */
const authService = {
  /**
   * Registra un nuevo cliente
   * @param {object} data - Datos del registro
   * @param {string} data.name - Nombre completo
   * @param {string} data.email - Correo electrónico
   * @param {string} data.phone - Teléfono
   * @param {string} data.password - Contraseña
   * @param {string} data.password_confirmation - Confirmación de contraseña
   * @param {string} [data.address] - Dirección opcional
   * @param {number} [data.zone_id] - ID de zona opcional
   * @returns {Promise<{user: object, token: string}>} Usuario y token
   */
  register: async (data) => {
    try {
      const response = await api.post('/customer/register', data);
      
      // El backend retorna { success, data: { customer, token }, message }
      const responseData = response.data?.data || response.data;
      const token = responseData?.token;
      const user = responseData?.customer || responseData?.user;
      
      if (token) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      }
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      }
      
      return { user, token };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Inicia sesión con credenciales
   * @param {object} credentials - Credenciales de acceso
   * @param {string} credentials.email - Correo electrónico
   * @param {string} credentials.password - Contraseña
   * @param {boolean} [credentials.remember] - Recordar sesión
   * @returns {Promise<{user: object, token: string}>} Usuario y token
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/customer/login', credentials);
      
      // El backend retorna { success, data: { customer, token }, message }
      const responseData = response.data?.data || response.data;
      const token = responseData?.token;
      const user = responseData?.customer || responseData?.user;
      
      if (token) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      }
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      }
      
      return { user, token };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Cierra la sesión del usuario
   * @returns {Promise<{message: string}>} Mensaje de confirmación
   */
  logout: async () => {
    try {
      const response = await api.post('/customer/logout');
      
      // Limpiar localStorage
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      
      return response.data;
    } catch (error) {
      // Aunque falle el logout en el servidor, limpiar localStorage
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      throw error;
    }
  },

  /**
   * Obtiene el perfil del usuario autenticado
   * @returns {Promise<object>} Datos del perfil
   */
  getProfile: async () => {
    try {
      const response = await api.get('/customer/profile');
      
      // El backend retorna { success, data: customer, message }
      const user = response.data?.data || response.data;
      
      // Actualizar usuario en localStorage
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      }
      
      return user;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Actualiza el perfil del usuario
   * @param {object} data - Datos a actualizar
   * @param {string} [data.name] - Nombre completo
   * @param {string} [data.phone] - Teléfono
   * @param {string} [data.address] - Dirección
   * @param {number} [data.zone_id] - ID de zona
   * @param {string} [data.avatar] - URL de avatar
   * @returns {Promise<object>} Perfil actualizado
   */
  updateProfile: async (data) => {
    try {
      const response = await api.put('/customer/profile', data);
      
      // El backend retorna { success, data: customer, message }
      const user = response.data?.data || response.data;
      
      // Actualizar usuario en localStorage
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      }
      
      return user;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Cambia la contraseña del usuario
   * @param {object} data - Datos de contraseña
   * @param {string} data.current_password - Contraseña actual
   * @param {string} data.password - Nueva contraseña
   * @param {string} data.password_confirmation - Confirmación de nueva contraseña
   * @returns {Promise<{message: string}>} Mensaje de confirmación
   */
  changePassword: async (data) => {
    try {
      const response = await api.post('/customer/password', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Solicita recuperación de contraseña
   * @param {object} data - Datos para recuperación
   * @param {string} data.email - Correo electrónico
   * @returns {Promise<{message: string}>} Mensaje de confirmación
   */
  forgotPassword: async (data) => {
    try {
      const response = await api.post('/customer/forgot-password', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Restablece la contraseña con token
   * @param {object} data - Datos para restablecer
   * @param {string} data.token - Token de recuperación
   * @param {string} data.email - Correo electrónico
   * @param {string} data.password - Nueva contraseña
   * @param {string} data.password_confirmation - Confirmación
   * @returns {Promise<{message: string}>} Mensaje de confirmación
   */
  resetPassword: async (data) => {
    try {
      const response = await api.post('/customer/reset-password', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verifica si el token actual es válido
   * @returns {Promise<boolean>} True si el token es válido
   */
  verifyToken: async () => {
    try {
      await api.get('/customer/profile');
      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Obtiene el token almacenado
   * @returns {string|null} Token o null
   */
  getToken: () => {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  },

  /**
   * Obtiene el usuario almacenado
   * @returns {object|null} Usuario o null
   */
  getStoredUser: () => {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    return user ? JSON.parse(user) : null;
  },

  /**
   * Verifica si hay una sesión activa
   * @returns {boolean} True si hay token
   */
  isAuthenticated: () => {
    return !!localStorage.getItem(STORAGE_KEYS.TOKEN);
  },
};

export default authService;
